import { LitElement, html } from 'lit';
import { when } from 'lit-html/directives/when.js';
import { classMap } from 'lit-html/directives/class-map.js';

import { sprintf } from '@libs/sprintf.js';
import { getText } from '@utils/i18n.js'; // TODO pass i18n strings in

import { recordIcon } from '@common/components/vst-ui-icon/svg/recordIcon.svg.js';
import { stopIcon } from '@common/components/vst-ui-icon/svg/stopIcon.svg.js';
import { keepPinIcon } from '@common/components/vst-ui-icon/svg/keepPinIcon.svg.js';
import { markIcon } from '@common/components/vst-ui-icon/svg/markIcon.svg.js';
import '@components/vst-style-tooltip/vst-style-tooltip.js';

import { globalStyles } from '@styles/vst-style-global.css.js';
import vstUiToolbarStyles from './vst-ui-toolbar.css.js';

export class VstUiToolbar extends LitElement {
  static get properties() {
    return {
      // #region private state reactive properties
      // #endregion
      // #region public reactive properties
      canControl: { type: Boolean, reflect: true },
      canKeep: { type: Boolean, reflect: true },
      collectBtnMoreText: { type: String },
      collectButtonEnabled: { type: Boolean },
      dataLoggingEnabled: { type: Boolean },
      dataMarksEnabled: { type: Boolean },
      isCollecting: { type: Boolean },
      keepButtonEnabled: { type: Boolean },
      notify: { type: String, reflect: true },
      showCollectButtonForTour: { type: Boolean },
      tourActive: { type: Boolean },
      // #endregion
    };
  }

  // static get observableProperties() {
  // 	return {
  //
  // 	}
  // }

  static get styles() {
    return [globalStyles, vstUiToolbarStyles];
  }

  #isReadyForTour = Promise.withResolvers();

  constructor() {
    super();
    this.canControl = false;
    this.canKeep = false;
    this.collectBtnMoreText = '';
    this.collectButtonEnabled = true;
    this.dataLoggingEnabled = false;
    this.isCollecting = false;
    this.keepButtonEnabled = true;
    this.notify = '';
    this.tourActive = false;
    this.showCollectButtonForTour = false;
  }

  // #region Lit Lifecycle Callbacks
  connectedCallback() {
    super.connectedCallback();
    const toolbarButtonEls = this.querySelectorAll('button');

    toolbarButtonEls.forEach(toolbarButtonEl => {
      toolbarButtonEl.addEventListener('click', e => {
        const { action } = e.currentTarget.dataset;

        /**
         * Toolbar button events
         *
         * @event data-action
         * @type {object}
         * @property {object} anchor - The element the action is anchored to
         * @property {string} orientation - top|right|bottom|left
         */
        this.dispatchEvent(
          new CustomEvent(action, {
            detail: {
              anchor: e.currentTarget,
              sourceEvent: e,
              orientation: 'bottom',
            },
          }),
        );
      });
    });

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line wc/require-listener-teardown
    document.addEventListener('keydown', e => {
      if (e.keyCode === 32 && !this._windowIsFocused) {
        if (this.canControl) {
          const collectBtn = this.shadowRoot.getElementById('collect_btn');
          if (!collectBtn.disabled) {
            this.#collectData();
          }
        } else if (this.canKeep) {
          const keepBtn = this.shadowRoot.querySelector('#keep_btn');
          if (!keepBtn.disabled) {
            this.#keepData();
          }
        }
      }
    });

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line wc/require-listener-teardown
    window.addEventListener('focusin', () => {
      this._windowIsFocused = true;
    });

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line wc/require-listener-teardown
    window.addEventListener('focusout', () => {
      this._windowIsFocused = false;
    });
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === 'keepButtonEnabled') {
        this.#keepButtonEnabledChanged();
      } else if (propName === 'tourActive') {
        if (this.tourActive) {
          this.#isReadyForTour.resolve();
        } else {
          this.#isReadyForTour = Promise.withResolvers();
        }
      }
    });
  }
  // #endregion

  // #region getters/setters
  get #collectActionLabel() {
    if (this.dataLoggingEnabled) return getText('Start Logging');
    return this.isCollecting ? getText('Stop') : getText('Collect');
  }

  get #collectButtonText() {
    if (!this.collectBtnMoreText) return this.#collectActionLabel;
    return sprintf(
      getText(
        '%s %s',
        'general',
        'Collect button label: action then collection mode. EX: "Collect Absorbance"',
      ),
      this.#collectActionLabel,
      this.collectBtnMoreText,
    );
  }

  get #collectTooltipText() {
    if (this.dataLoggingEnabled) return getText('Start Logging');
    return this.isCollecting ? getText('Stop Collection') : getText('Start Collection');
  }

  /**
   * @returns {Promise<void>} Promise that resolves when collect button is ready for tour.
   */
  get isReadyForTour() {
    return this.#isReadyForTour.promise;
  }

  // #endregion

  // #region private methods
  /**
   * Toggles start/stop collection
   */
  #collectData() {
    const collectBtn = this.shadowRoot.getElementById('collect_btn');
    if (this.collectButtonEnabled) {
      this.collectButtonEnabled = false;
      if (this.isCollecting) {
        this.dispatchEvent(new CustomEvent('stop-collection'));
      } else {
        this.dispatchEvent(new CustomEvent('start-collection'));
      }
      setTimeout(() => {
        // prevent users from spamming the collect btn since sometimes it puts us in a bad state.
        this.collectButtonEnabled = true;
      }, 500);
    }

    collectBtn.classList.toggle('is-loading');
    // remove the loading class no matter what after 4 seconds.
    setTimeout(() => {
      collectBtn.classList.remove('is-loading');
    }, 8000);
  }

  #keepButtonEnabledChanged() {
    const keepBtn = this.shadowRoot.querySelector('#keep_btn');

    if (keepBtn && this.canKeep) {
      if (this.keepButtonEnabled) {
        keepBtn.focus();
      } else {
        keepBtn.blur();
      }
    }
  }

  #keepData() {
    /**
     * Keep point event
     *
     * @event keep-point
     */
    this.dispatchEvent(new CustomEvent('keep-point'));
  }

  #markData() {
    this.dispatchEvent(
      new CustomEvent('mark-data', {
        bubbles: true,
        composed: true,
      }),
    );
  }
  // #endregion

  // #region public methods
  /**
   * Sets focus on the #keep_btn element
   */
  focusKeep() {
    this.shadowRoot.querySelector('#keep_btn').focus();
  }
  // #endregion

  // #region template methods
  // #endregion

  render() {
    return html`
      <div
        class="toolbar"
        id="toolbar"
        ?connected="${this.connected}"
        ?canControl="${this.canControl}"
        ?canKeep="${this.canKeep}"
        ?isCollecting="${this.isCollecting}"
      >
        <div class="left-content">
          <slot name="toolbar_left"></slot>
        </div>

        <div class="center-content">
          <slot name="toolbar_center"></slot>
          ${when(
            this.canControl || (this.tourActive && this.showCollectButtonForTour),
            () => html`
              <vst-style-tooltip>
                <button
                  aria-label="${this.#collectButtonText}"
                  class="btn toolbar-center-button"
                  id="collect_btn"
                  ?disabled="${!this.collectButtonEnabled}"
                  ?inert="${this.tourActive}"
                  @click="${this.#collectData}"
                >
                  <span aria-hidden="true" class="toolbar-center-icon">
                    ${this.isCollecting ? stopIcon : recordIcon}
                  </span>
                  <div
                    aria-hidden="true"
                    class="toolbar-center-label ${classMap({
                      'toolbar-center-two-line': this.collectBtnMoreText,
                    })}"
                  >
                    <div>${this.#collectActionLabel}</div>
                    ${when(
                      this.collectBtnMoreText,
                      () => html`
                        <div class="toolbar-center-second-line">${this.collectBtnMoreText}</div>
                      `,
                    )}
                  </div>
                </button>
                <span role="tooltip" position="block-end">${this.#collectTooltipText}</span>
              </vst-style-tooltip>
            `,
          )}
          ${when(
            this.canKeep && this.canControl,
            () => html`
              <vst-style-tooltip>
                <button
                  aria-labelledby="keep-btn-tooltip-text"
                  class="btn toolbar-center-button"
                  id="keep_btn"
                  variant="secondary"
                  ?disabled="${!this.keepButtonEnabled}"
                  @click="${this.#keepData}"
                >
                  <span aria-hidden="true" class="toolbar-center-icon">${keepPinIcon}</span>
                  <span class="toolbar-center-label">${getText('Keep')}</span>
                </button>
                <span id="keep-btn-tooltip-text" role="tooltip" position="block-end">
                  ${getText('Keep data point')}
                </span>
              </vst-style-tooltip>
            `,
          )}
          ${when(
            this.dataMarksEnabled && this.canControl,
            () => html`
              <vst-style-tooltip>
                <button
                  aria-labelledby="mark-btn-tooltip-text"
                  class="btn toolbar-center-button"
                  id="mark_btn"
                  variant="secondary"
                  ?disabled=${!this.isCollecting}
                  @click="${this.#markData}"
                >
                  <span aria-hidden="true" class="toolbar-center-icon">${markIcon}</span>
                  <span class="toolbar-center-label">${getText('Mark')}</span>
                </button>
                <span id="mark-btn-tooltip-text" role="tooltip" position="block-end">
                  ${getText('Add data mark')}
                </span>
              </vst-style-tooltip>
            `,
          )}
        </div>

        <div class="right-content">
          <slot name="toolbar_right"></slot>
        </div>
      </div>
      <div class="replay-content">
        <slot name="toolbar_replay"></slot>
      </div>
    `;
  }
}
customElements.define('vst-ui-toolbar', VstUiToolbar);
